<template>
  <div
    class="whiteboard-canvas"
    @contextmenu="onContextMenu"
    @mousemove="onMouseMove"
    @mouseover="showBrushCircle = false;"
    @mouseout="showBrushCircle = false"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp">
    <app-overlay-loading :loading="loading"/>
    <div ref="brushCircle" class="brush-circle" :style="brushCircleStyle"></div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import Whiteboard from './lib/whiteboard';
import ZoomHelper from './lib/zoom-helper';
import fileSaveHelper from '@/lib/file-save-helper';

let wb = {};
let zoomHelper = {};

export default {
  data() {
    return {
      loading: false,
      showBrushCircle: false
    };
  },

  computed: {
    ...mapState('whiteboard', {
      mode: state => state.mode,
      scale: state => state.scale,
      minScale: state => state.minScale,
      maxScale: state => state.maxScale,
      brushColor: state => state.brushColor,
      brushWidth: state => state.brushWidth,
      boards: state => state.boards
    }),

    showBackgroundImage: {
      get() {
        return this.$store.state.whiteboard.showBackgroundImage;
      },
      set(v) {
        this.$store.commit('whiteboard/updateStates', {showBackgroundImage: v});
      }
    },

    page: {
      get() {
        return this.$store.state.whiteboard.page;
      },
      set(v) {
        if (v === this.page) {
          return;
        }
        this.$store.commit('whiteboard/updateStates', {page: v});
      }
    },

    scale: {
      get() {
        return this.$store.state.whiteboard.scale;
      },
      set(v) {
        this.$store.commit('whiteboard/updateStates', {scale: v});
      }
    },

    brushCircleStyle() {
      return {
        'display': this.showBrushCircle ? 'block' : 'none',
        'width': this.brushWidth + 'px',
        'height': this.brushWidth + 'px'
      };
    }
  },

  watch: {
    boards() {
      this.updateBoards();
    },
    async page() {
      await wb.switchPage(this.page);
      this.showBackgroundImage = wb.isBackgroundImageVisible;
    },
    brushColor(v) {
      wb.brushColor = v;
    },
    brushWidth(v) {
      wb.brushWidth = v;
    },
    mode(v) {
      switch (v) {
        case 'pencil':
          wb.eraser = false;
          wb.freeDrawing = true;
          zoomHelper.draggable = false;
          break;
        case 'eraser':
          wb.eraser = true;
          wb.freeDrawing = true;
          zoomHelper.draggable = false;
          break;

        case 'drag': {
          wb.cursor = 'move';
          wb.freeDrawing = false;
          zoomHelper.draggable = true;
          break;
        }
      }
    },
    scale(v) {
      zoomHelper.setScale(v);
    },
    showBackgroundImage(v) {
      wb.setBackgroundImageVisible(v);
    }
  },

  async mounted() {
    this.$eventBus.$on('whiteboard-switch-board', this.switchBoard);

    wb = new Whiteboard(this.$el);
    wb.brushColor = this.brushColor;
    wb.brushWidth = this.brushWidth;

    zoomHelper = new ZoomHelper(wb.getUpperCanvas(), {
      maxScale: this.maxScale,
      minScale: this.minScale,
      zoomAnimate: false,
      onWheel: event => {
        // disable zooming when user is drawing.
        if (this._isDrawing === true) {
          return false;
        }
        return true;
      },
      onZoom: (info, isEnd) => {
        wb.setTransform(info.scale, info.translateX, info.translateY, isEnd);
        this.scale = info.scale;
      },
      onDrag: (info) => wb.setTransform(info.scale, info.translateX, info.translateY)
    });

    this.updateBoards();
    // this.loading = true;
    // try {
    //   // await wb.loadImage('https://picsum.photos/1024/768?random');
    //   // await wb.loadImage('https://drscdn.500px.org/photo/265226291/q%3D80_m%3D1500/v2?user_id=80358&webp=true&sig=a2bb090ee9edd0eade9bea61138d3e6a802d94a86bab64f7e2a2e011c165ca39');
    //   // wb.addBoards([
    //   //   '/img/test.jpg',
    //   //   'https://picsum.photos/2048/1024?random',
    //   //   'https://drscdn.500px.org/photo/265226291/q%3D80_m%3D1500/v2?user_id=80358&webp=true&sig=a2bb090ee9edd0eade9bea61138d3e6a802d94a86bab64f7e2a2e011c165ca39'
    //   // ]);
    //   await wb.switchBoard(1);
    // } catch (e) {
    //   console.error(e);
    // } finally {
    //   this.loading = false;
    // }
  },

  beforeDestroy() {
    this.$eventBus.$off('whiteboard-switch-board', this.switchBoard);
  },

  methods: {
    onContextMenu(e) {
      // disable right click menu
      e.preventDefault();
    },

    // TODO
    // brush circle does not stick very close to cursor, so comment the code
    // until we solve it.
    onMouseMove(e) {
      // let brushCircle = this.$refs.brushCircle;
      // brushCircle.style.left = e.clientX + 'px';
      // brushCircle.style.top = e.clientY + 'px';
    },

    onMouseDown(e) {
      if (e.button === 0) {
        this._isDrawing = true;
      } else {
        // disalbe middle & right click
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
      }
    },

    onMouseUp(e) {
      if (e.button === 0) {
        this._isDrawing = false;
      } else {
        // disalbe middle & right click
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
      }
    },

    undo() {
      wb.undo();
    },

    redo() {
      wb.redo();
    },

    clearObjects() {
      wb.clearObjects();
    },

    rotateBackgroundImage() {
      wb.rotateBackgroundImage();
    },

    async switchBoard(id) {
      this.page = 0;
      await wb.switchBoard(id);
    },

    async updateBoards() {
      // remove boards
      for (let key in wb.boards) {
        if (!wb.boards.hasOwnProperty(key)) {
          continue;
        }

        if (this.boards.findIndex(board => board.id === key) < 0) {
          wb.removeBoard(key);
        }
      }

      // add boards
      this.boards.forEach(board => {
        if (!wb.boards[board.id]) {
          wb.addBoard(board.id, board.url);
        }
      });

      if (!this.boards.length) {
        wb.addBoard('tmp', null);
        await wb.switchBoard('tmp');
      }
    },

    async saveAsImage(filename) {
      let blob = await wb.getBlob();
      fileSaveHelper.saveBlobFile(blob, filename);
    }
  }
};
</script>

<style lang="stylus" scoped>
.whiteboard-canvas {
  position: relative;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.loading {
  position: absolute;
  margin: auto;
  z-index: 1;
}

.brush-circle {
  display: none;
  position: fixed;
  border-radius: 50%;
  border: 1px dashed black;
  transform: translate(-50%, -50%);
  z-index: 0;
}
</style>
